import React, { useEffect } from "react";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField/TextField";
import { fetchUser } from "../../../actions/Management/userManagementActions";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import InstagramIcon from "@material-ui/icons/Instagram";
import TwitterIcon from "@material-ui/icons/Twitter";
import { sentenceCheck } from "../../../utils/sentenceCheck";

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      marginTop: 60,
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(2),
      top: theme.spacing(2),
      color: theme.palette.type === "dark" ? "#fff" : "#181C1E",
      border: "1px solid #EAEDF0",
      [theme.breakpoints.down("xs")]: {
        top: theme.spacing(1),
      },
    },
    avatar: { width: 230, height: 230 },
    title: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    textFields: {
      height: 60,
      width: 250,
    },
    settingsSection: {
      marginBottom: theme.spacing(1),
    },
  })
);

const UserDetail = ({ userId, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useSelector((state) => state.userManagement.user);

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [profilePicture, setProfilePicture] = React.useState(
    user?.profilePicture
  );
  const [birthDate, setBirthDate] = React.useState("");
  const [gender, setGender] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [position, setPosition] = React.useState("");
  const [linkedinLink, setLinkedinLink] = React.useState("");
  const [instagramLink, setInstagramLink] = React.useState("");
  const [twitterLink, setTwitterLink] = React.useState("");

  useEffect(() => {
    fetchUserById();
  }, [userId]);

  useEffect(() => {
    if (user?.id) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setUsername(user.username);
      setEmail(user.email);
      setPhoneNumber(user.phoneNumber);
      setProfilePicture(user.profilePicture);
      setBirthDate(user.birthDate);
      setGender(user.gender);
      setLocation(user.location);
      setPosition(user.position);
      setLinkedinLink(user.linkedinLink);
      setInstagramLink(user.instagramLink);
      setTwitterLink(user.twitterLink);
    }
  }, [user]);

  function fetchUserById() {
    dispatch(fetchUser(userId));
  }

  const handleOnClose = () => {
    onClose();
  };
  return (
    <div>
      {!userId || Object.entries(user).length ? (
        <Dialog className={classes.dialog} open={true} fullScreen={fullScreen}>
          <DialogTitle
            id="responsive-dialog-title"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div className={classes.title}>
              {user.firstName && (
                <Avatar
                  className={classes.avatar}
                  displayName={[user.firstName, user.lastName].join(" ")}
                  src={profilePicture}
                  id={user?.id}
                />
              )}
              <h3 style={{ marginTop: 5 }}>
                {[
                  user.firstName,
                  user.lastName,
                  user.fromLdap ? " (LDAP)" : "",
                ].join(" ")}
              </h3>
            </div>
          </DialogTitle>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleOnClose}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <div className={classes.settingsSection}>
              <Typography variant="h6">
                <FormattedMessage
                  id="management.profile"
                  defaultMessage="Profile"
                />
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="firstName"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.firstname",
                      defaultMessage: "First Name",
                    })}
                    color="primary"
                    value={firstName}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="lastName"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.lastname",
                      defaultMessage: "Last Name",
                    })}
                    color="primary"
                    value={lastName}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="birthDate"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.birthDate",
                      defaultMessage: "Birth Date",
                    })}
                    color="primary"
                    readOnly
                    value={birthDate ? birthDate : "-"}
                    variant="standard"
                    margin="normal"
                    readOnly
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="gender"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.gender",
                      defaultMessage: "Gender",
                    })}
                    color="primary"
                    value={gender ? gender : "-"}
                    variant="standard"
                    margin="normal"
                    readOnly
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  ></TextField>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="location"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.location",
                      defaultMessage: "Location",
                    })}
                    color="primary"
                    readOnly
                    value={location ? location : "-"}
                    variant="standard"
                    margin="normal"
                    readOnly
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="position"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.position",
                      defaultMessage: "Position",
                    })}
                    readOnly
                    color="primary"
                    value={position ? position : "-"}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </div>

            <div className={classes.settingsSection}>
              <Typography variant="h6">
                <FormattedMessage
                  id="management.account"
                  defaultMessage="Account"
                />
              </Typography>
              <Grid container spacing={3}>
                {true && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="username"
                      className={classes.textFields}
                      label={intl.formatMessage({
                        id: "management.user.username",
                        defaultMessage: "User Name",
                      })}
                      color="primary"
                      value={username}
                      variant="standard"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                        readOnly: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </div>

            <div className={classes.settingsSection}>
              <Typography variant="h6">
                <FormattedMessage
                  id="management.contactInfo"
                  defaultMessage="Contact Info"
                />
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="username"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.email",
                      defaultMessage: "Email",
                    })}
                    color="primary"
                    value={email}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="phoneNumber"
                    className={classes.textFields}
                    label={intl.formatMessage({
                      id: "management.user.phoneNumber",
                      defaultMessage: "Phone Number",
                    })}
                    color="primary"
                    value={phoneNumber}
                    variant="standard"
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1} direction="column">
                <Typography variant="h6">
                  <FormattedMessage
                    id="management.socialMediaInfo"
                    defaultMessage="Social Media Info"
                  />
                </Typography>
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <LinkedInIcon style={{ color: "#0e76a8" }} />
                  </Grid>
                  <Grid item xs={12} sm={11}>
                    <TextField
                      id="linkedinLink"
                      className={classes.socialMediaTextFields}
                      label={intl.formatMessage({
                        id: "management.user.linkedinLink",
                        defaultMessage: "Linkedin",
                      })}
                      color="primary"
                      readOnly
                      value={linkedinLink ? linkedinLink : "-"}
                      variant="standard"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <InstagramIcon
                      style={{
                        color: "white",
                        background:
                          "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={11}>
                    <TextField
                      id="instagramLink"
                      className={classes.socialMediaTextFields}
                      label={intl.formatMessage({
                        id: "management.user.instagramLink",
                        defaultMessage: "Instagram",
                      })}
                      color="primary"
                      readOnly
                      value={instagramLink ? instagramLink : "-"}
                      variant="standard"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <TwitterIcon style={{ color: "#1DA1F2" }} />
                  </Grid>
                  <Grid item xs={12} sm={11}>
                    <TextField
                      id="twitterLink"
                      className={classes.socialMediaTextFields}
                      label={intl.formatMessage({
                        id: "management.user.twitterLink",
                        defaultMessage: "Twitter",
                      })}
                      color="primary"
                      readOnly
                      value={twitterLink ? twitterLink : "-"}
                      variant="standard"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog className={classes.dialog} open={true} fullScreen={fullScreen}>
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UserDetail;
